import React from "react";
import CTADoubleBlock from "../components/CTADoubleBlock/CTADoubleBlock";
import EmployeesBlockWrapper from "../components/EmployeesBlockWrapper/EmployeesBlockWrapper";
import FullImageBlockWrapper from "../components/FullImageBlockWrapper/FullImageBlockWrapper";
import ProductSection from "../components/ProductSection/ProductSection";
import SocialMedia from "../components/SocialMedia/SocialMedia";
import StatisticsBlock from "../components/StatisticsBlock/StatisticsBlock";
import TeaserListBlock from "../components/TeaserListBlock/TeaserListBlock";
import TextAndImageBlock from "../components/TextAndImageBlock/TextAndImageBlock";
import TextBlock from "../components/TextBlock/TextBlock";
import TwoColumnsComponent from "../components/TwoColumnsComponent/TwoColumnsComponent";
import SpacerBlock from "../components/SpacerBlock/SpacerBlock";
import AccordionBlock from "../components/AccordionBlock/AccordionBlock";
import {
  isTeaserListBlock,
  isCTADoubleBlock,
  isTextAndImageBlock,
  isProductsBlock,
  isStatsNumbersBlock,
  isFullImageBlock,
  isEmployeeTeaserListBlock,
  isSocialMediaWallBlock,
  isTextBlock,
  isTwoColumnsBlock,
  isSpacerBlock,
  isAccordionBlock,
  isLinkListBlock,
  isEditorialBlock,
  isCTABlock,
  isContactBlock,
  isColumnsBlock,
  isPagePreviewBlock,
  isAnchorBlock,
  isIFrameBlock,
} from "../types/optimizely/blocks";
import { OptimizelyExpandedValue } from "../types/optimizely/expanded";
import LinkListBlock from "../components/LinkListBlock/LinkListBlock";
import EditorialBlock from "../components/EditorialBlock/EditorialBlock";
import CTABlock from "../components/CTABlock/CTABlock";
import { mapContactBlockToCTABlockProps } from "./mamDTOToProps";
import ColumnContentRowBlock from "../components/ColumnContentRowBlock/ColumnContentRowBlock";
import PagePreviewBlock from "../components/PagePreviewBlock/PagePreviewBlock";
import AnchorBlock from "../components/AnchorBlock/AnchorBlock";
import IFrameBlock from "../components/IFrameBlock/IFrameBlock";

interface OverridesForClasses {
  [key: string]: {
    [key: string]: string;
  };
}

export const mapContentToBlock = (
  content: OptimizelyExpandedValue,
  isFrontPage?: boolean,
  overridesForClasses?: OverridesForClasses
) => {
  if (!content?.contentType) {
    return <></>;
  }

  if (isTeaserListBlock(content)) {
    return (
      <TeaserListBlock
        content={content.content}
        allNewsLink={content.headerLink.value}
        allNewsLabel={content.headerLinkText.value}
        sectionLabel={content.name}
        navigateToArticleText={content.navigateToArticleText.value}
        sectionId={content.sectionId?.value}
      />
    );
  }

  if (isCTADoubleBlock(content)) {
    return (
      <CTADoubleBlock
        ctaLeft={content.ctaLeft}
        ctaRight={content.ctaRight}
        sectionId={content.sectionId?.value}
      />
    );
  }

  if (isTextAndImageBlock(content)) {
    const overrides = overridesForClasses?.[content.contentType[1]];
    const overridesForRightClasses = overrides?.overridesForRightClasses;
    const overridesForTextClasses = overrides?.overridesForTextClasses;

    return (
      <TextAndImageBlock
        {...content}
        overridesForRightClasses={overridesForRightClasses}
        overridesForTextClasses={overridesForTextClasses}
      />
    );
  }

  if (isTextBlock(content)) {
    return <TextBlock {...content} />;
  }

  if (isEditorialBlock(content)) {
    return (
      <EditorialBlock
        {...content}
        backgroundColor={{
          value: "transparent",
          propertyDataType: "PropertyLongString",
        }}
      />
    );
  }

  if (isSpacerBlock(content)) {
    return <SpacerBlock {...content} />;
  }

  if (isCTABlock(content)) {
    return <CTABlock {...content} />;
  }

  if (isContactBlock(content)) {
    const ctaProps = mapContactBlockToCTABlockProps(content);

    return <CTABlock {...ctaProps} />;
  }

  if (isTwoColumnsBlock(content)) {
    return <TwoColumnsComponent {...content} />;
  }

  if (isAccordionBlock(content)) {
    return <AccordionBlock {...content} />;
  }

  if (isProductsBlock(content)) {
    return <ProductSection module={content} />;
  }

  if (isStatsNumbersBlock(content)) {
    return <StatisticsBlock content={content} />;
  }

  if (isFullImageBlock(content)) {
    return <FullImageBlockWrapper module={content} />;
  }

  if (isEmployeeTeaserListBlock(content)) {
    return <EmployeesBlockWrapper module={content} isFrontPage={isFrontPage} />;
  }

  if (isSocialMediaWallBlock(content)) {
    return (
      <SocialMedia
        feedId={content.juicerFeedId.value}
        pagination={{ pageSize: content.numberOfItems.value }}
        heading={content.heading.value}
      />
    );
  }

  if (isLinkListBlock(content)) {
    return <LinkListBlock title={content.title} links={content.links} />;
  }

  if (isColumnsBlock(content)) {
    return <ColumnContentRowBlock {...content} />;
  }

  if (isPagePreviewBlock(content)) {
    return <PagePreviewBlock {...content} />;
  }

  if (isAnchorBlock(content)) {
    return <AnchorBlock {...content} />;
  }

  if (isIFrameBlock(content)) {
    return <IFrameBlock {...content} />;
  }
};
